import { GizmoHelper, GizmoViewport } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { EmptyState } from '@uag/react-core';
import { useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Object3D } from 'three';

import { HubConnectionContainer } from 'app/modules/wallforming/components/content-container/HubConnectionContainer';
import { FrustumCameraControls } from 'app/modules/wallforming/components/scenes/camera-controls/FrustumCameraControls';
import { FrustumViewerScene } from 'app/modules/wallforming/components/scenes/FrustumViewerScene';

/**
 * CameraFrustumViewer — Standalone 3D viewer for visualizing the main scene camera's frustum and related geometry.
 *
 * Renders a Three.js scene in a separate browser tab, displaying the building, articles, and camera frustum helper.
 * Scene data is synchronized from the main app via postMessage (see useFrustumViewerSyncHandler and useFrustumMessageHandler).
 *
 * The viewer camera automatically frames the main scene's frustum on first sync, providing an immediate overview for debugging or inspection.
 */
export const CameraFrustumViewer = () => {
    Object3D.DEFAULT_UP.set(0, 0, 1);

    const cameraControlsRef = useRef<{ frameBuilding: () => void } | null>(null);

    const handleOnDoubleClick = () => {
        cameraControlsRef.current?.frameBuilding();
    };

    return (
        <div
            style={{ width: '100vw', height: '100vh', overflow: 'hidden', background: '#1a1a2e', position: 'relative' }}
        >
            <ErrorBoundary
                key={location.pathname}
                fallbackRender={({ error }) => (
                    <EmptyState
                        action={() => window.location.reload()}
                        actionLabel="Refresh"
                        className="h-full w-full"
                        type="error"
                    >
                        {error instanceof Error ? error.message : String(error)}
                    </EmptyState>
                )}
            >
                <HubConnectionContainer>
                    <Canvas
                        camera={{ fov: 50, near: 0.01, far: 100_000, position: [0, 40, 80] }}
                        frameloop="demand"
                        gl={{ antialias: true }}
                        style={{ background: '#1a1a2e' }}
                        orthographic
                        onDoubleClick={handleOnDoubleClick}
                    >
                        <GizmoHelper alignment="top-right">
                            <GizmoViewport axisColors={['red', 'green', 'blue']} labelColor="white" />
                        </GizmoHelper>
                        <FrustumCameraControls ref={cameraControlsRef} />
                        <FrustumViewerScene />
                    </Canvas>
                </HubConnectionContainer>
            </ErrorBoundary>

            <div
                style={{
                    position: 'absolute',
                    top: 10,
                    left: 12,
                    color: '#90caf9',
                    fontFamily: 'monospace',
                    fontSize: 11,
                    lineHeight: '1.6',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    background: 'rgba(0,0,0,0.45)',
                    padding: '4px 8px',
                    borderRadius: 4
                }}
            >
                <strong
                    style={{
                        fontSize: 14
                    }}
                >
                    Camera Frustum Viewer
                </strong>
                <br />
                Left-drag to rotate · Scroll to zoom · Right-drag to pan · Double-click to focus building
            </div>
        </div>
    );
};
